import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateAccountStatus,
  disconnectAccount,
  performBulkAccountOperations,
  exportAccountsData,
  downloadCSV,
  AccountOperation,
  BulkOperationResult
} from '@/services/admin/prop-bot-account-operations-service';
import { PropBotConnectedAccount } from './use-prop-bot-detail-admin';
import { toast } from 'sonner';

export const usePropBotOperations = (botId: string | undefined) => {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['propBotConnectedAccounts', botId] });
  };

  // Single account operations
  const enableAccountMutation = useMutation({
    mutationFn: (accountId: string) => updateAccountStatus(accountId, 'active'),
    onSuccess: () => {
      invalidateQueries();
      toast.success('Account enabled successfully');
    },
    onError: (error) => {
      toast.error(`Failed to enable account: ${error.message}`);
    }
  });

  const disableAccountMutation = useMutation({
    mutationFn: (accountId: string) => updateAccountStatus(accountId, 'inactive'),
    onSuccess: () => {
      invalidateQueries();
      toast.success('Account disabled successfully');
    },
    onError: (error) => {
      toast.error(`Failed to disable account: ${error.message}`);
    }
  });

  const disconnectAccountMutation = useMutation({
    mutationFn: disconnectAccount,
    onSuccess: () => {
      invalidateQueries();
      toast.success('Account disconnected successfully');
    },
    onError: (error) => {
      toast.error(`Failed to disconnect account: ${error.message}`);
    }
  });

  // Bulk operations
  const bulkOperationsMutation = useMutation({
    mutationFn: performBulkAccountOperations,
    onSuccess: (result: BulkOperationResult) => {
      invalidateQueries();
      
      if (result.success > 0 && result.failed === 0) {
        toast.success(`Successfully processed ${result.success} account${result.success !== 1 ? 's' : ''}`);
      } else if (result.success > 0 && result.failed > 0) {
        toast.warning(`Processed ${result.success} accounts, ${result.failed} failed. Errors: ${result.errors.join(', ')}`);
      } else {
        toast.error(`All operations failed. Errors: ${result.errors.join(', ')}`);
      }
    },
    onError: (error) => {
      toast.error(`Bulk operation failed: ${error.message}`);
    }
  });

  // Export functionality
  const handleExport = (accounts: PropBotConnectedAccount[], filename?: string) => {
    try {
      const csvData = exportAccountsData(accounts);
      const defaultFilename = `prop-bot-accounts-${botId}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvData, filename || defaultFilename);
      toast.success('Account data exported successfully');
    } catch (error) {
      toast.error('Failed to export account data');
    }
  };

  // Bulk operation helpers
  const performBulkEnable = (accountIds: string[]) => {
    const operations: AccountOperation[] = accountIds.map(id => ({
      accountId: id,
      action: 'enable'
    }));
    return bulkOperationsMutation.mutateAsync(operations);
  };

  const performBulkDisable = (accountIds: string[]) => {
    const operations: AccountOperation[] = accountIds.map(id => ({
      accountId: id,
      action: 'disable'
    }));
    return bulkOperationsMutation.mutateAsync(operations);
  };

  const performBulkDisconnect = (accountIds: string[]) => {
    const operations: AccountOperation[] = accountIds.map(id => ({
      accountId: id,
      action: 'disconnect'
    }));
    return bulkOperationsMutation.mutateAsync(operations);
  };

  return {
    // Single operations
    enableAccount: enableAccountMutation.mutateAsync,
    disableAccount: disableAccountMutation.mutateAsync,
    disconnectAccount: disconnectAccountMutation.mutateAsync,
    
    // Bulk operations
    performBulkEnable,
    performBulkDisable,
    performBulkDisconnect,
    
    // Export
    exportAccounts: handleExport,
    
    // Loading states
    isEnabling: enableAccountMutation.isPending,
    isDisabling: disableAccountMutation.isPending,
    isDisconnecting: disconnectAccountMutation.isPending,
    isBulkProcessing: bulkOperationsMutation.isPending,
    
    // General processing state
    isProcessing: enableAccountMutation.isPending || 
                  disableAccountMutation.isPending || 
                  disconnectAccountMutation.isPending || 
                  bulkOperationsMutation.isPending,
  };
};